import React, { useState, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import moment from "moment";

const useStyles = makeStyles()(theme => ({
    wrap: { position: "relative" },
    svg: { display: "block", width: "100%", height: "auto", overflow: "visible" },
    grid: { stroke: theme.palette.mode === "light" ? "#eeede7" : "#252523", strokeWidth: 1 },
    axisText: {
        fontSize: 10,
        fill: theme.palette.text.disabled,
        fontVariantNumeric: "tabular-nums",
    },
    dotRing: { stroke: theme.palette.mode === "light" ? "#ffffff" : "#1a1a19", strokeWidth: 2 },
    crosshair: {
        stroke: theme.palette.text.disabled,
        strokeWidth: 1,
        strokeDasharray: "3 3",
        pointerEvents: "none",
    },
    legend: {
        display: "flex",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontSize: 12,
    },
    legendItem: { display: "flex", alignItems: "center", gap: 6 },
    legendSwatch: { width: 9, height: 9, borderRadius: 2, flexShrink: 0 },
    tooltip: {
        position: "absolute",
        pointerEvents: "none",
        background: theme.palette.mode === "light" ? "#ffffff" : "#26262b",
        border: `1px solid ${theme.palette.mode === "light" ? "#dfe3ea" : "#33333a"}`,
        borderRadius: 6,
        padding: theme.spacing(0.75, 1),
        fontSize: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,.14)",
        whiteSpace: "nowrap",
        zIndex: 3,
    },
    tipDay: { fontWeight: 700, marginBottom: 2 },
    tipRow: { display: "flex", alignItems: "center", gap: 6 },
    tipNum: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
    empty: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        padding: theme.spacing(2, 0),
    },
}));

const curva = (pts) => {
    const n = pts.length;
    if (n === 0) return "";
    if (n === 1) return `M${pts[0].x},${pts[0].y}`;
    if (n === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;

    const d = [];
    for (let i = 0; i < n - 1; i++) {
        d.push((pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x));
    }

    const m = [d[0]];
    for (let i = 1; i < n - 1; i++) {
        m.push(d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2);
    }
    m.push(d[n - 2]);

    for (let i = 0; i < n - 1; i++) {
        if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
        const a = m[i] / d[i];
        const b = m[i + 1] / d[i];
        const s = a * a + b * b;
        if (s > 9) {
            const t = 3 / Math.sqrt(s);
            m[i] = t * a * d[i];
            m[i + 1] = t * b * d[i];
        }
    }

    let out = `M${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < n - 1; i++) {
        const h = (pts[i + 1].x - pts[i].x) / 3;
        out += ` C${pts[i].x + h},${pts[i].y + m[i] * h}` +
               ` ${pts[i + 1].x - h},${pts[i + 1].y - m[i + 1] * h}` +
               ` ${pts[i + 1].x},${pts[i + 1].y}`;
    }
    return out;
};

const W = 720;
const H = 170;
const AIRE = 0.9;

const PAD = { top: 12, right: 10, bottom: 22, left: 30 };

const DailyTrend = ({ data, colorIncoming, colorClosed, labelIncoming, labelClosed }) => {
    const { classes } = useStyles();
    const [hover, setHover] = useState(null);

    const calc = useMemo(() => {
        if (!data || data.length === 0) return null;
        const max = Math.max(1, ...data.map(d => Math.max(d.incoming, d.closed)));
        const innerW = W - PAD.left - PAD.right;
        const innerH = H - PAD.top - PAD.bottom;
        const x = (i) => data.length === 1
            ? PAD.left + innerW / 2
            : PAD.left + (i / (data.length - 1)) * innerW;
        const y = (v) => PAD.top + innerH - (v / max) * innerH * AIRE;
        const puntos = (key) => data.map((d, i) => ({ x: x(i), y: y(d[key]) }));
        const linea = (key) => curva(puntos(key));
        const area = (key) => {
            const pts = puntos(key);
            const base = PAD.top + innerH;
            return `${curva(pts)} L${pts[pts.length - 1].x},${base} L${pts[0].x},${base} Z`;
        };
        const iMaxIn = data.reduce((b, d, i) => d.incoming > data[b].incoming ? i : b, 0);
        const iMaxCl = data.reduce((b, d, i) => d.closed > data[b].closed ? i : b, 0);
        return {
            max, x, y, innerH,
            pathIn: linea("incoming"), pathCl: linea("closed"),
            areaIn: area("incoming"), areaCl: area("closed"),
            iMaxIn, iMaxCl
        };
    }, [data]);

    if (!calc) return <div className={classes.empty}>—</div>;

    const paso = Math.ceil(data.length / 8);

    const mover = (e) => {
        const caja = e.currentTarget.getBoundingClientRect();
        const rel = ((e.clientX - caja.left) / caja.width) * W;
        const innerW = W - PAD.left - PAD.right;
        const i = data.length === 1
            ? 0
            : Math.round(((rel - PAD.left) / innerW) * (data.length - 1));
        setHover(Math.max(0, Math.min(data.length - 1, i)));
    };

    const punto = hover != null ? data[hover] : null;

    return (
        <div className={classes.wrap}>
            <div className={classes.legend}>
                <span className={classes.legendItem}>
                    <i className={classes.legendSwatch} style={{ background: colorIncoming }} />
                    {labelIncoming}
                </span>
                <span className={classes.legendItem}>
                    <i className={classes.legendSwatch} style={{ background: colorClosed }} />
                    {labelClosed}
                </span>
            </div>

            <svg
                className={classes.svg}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                onMouseMove={mover}
                onMouseLeave={() => setHover(null)}
                role="img"
                aria-label={`${labelIncoming} y ${labelClosed} por día`}
            >
                {[0, 0.5, 1].map((f) => {
                    const v = Math.round(calc.max * (1 - f));
                    const yy = calc.y(v);
                    return (
                        <g key={f}>
                            <line className={classes.grid} x1={PAD.left} x2={W - PAD.right} y1={yy} y2={yy} />
                            <text className={classes.axisText} x={0} y={yy + 3}>{v}</text>
                        </g>
                    );
                })}

                {data.map((d, i) => (i % paso === 0 || i === data.length - 1) && (
                    <text key={i} className={classes.axisText} x={calc.x(i)} y={H - 6} textAnchor="middle">
                        {moment(d.day).format("DD/MM")}
                    </text>
                ))}

                {hover != null && (
                    <line className={classes.crosshair} x1={calc.x(hover)} x2={calc.x(hover)}
                          y1={PAD.top} y2={PAD.top + calc.innerH} />
                )}

                <defs>
                    {}
                    {[["gradIn", colorIncoming], ["gradCl", colorClosed]].map(([id, c]) => (
                        <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={c} stopOpacity="0.24" />
                            <stop offset="100%" stopColor={c} stopOpacity="0.02" />
                        </linearGradient>
                    ))}
                </defs>

                <path d={calc.areaCl} fill="url(#gradCl)" />
                <path d={calc.areaIn} fill="url(#gradIn)" />

                <path d={calc.pathCl} fill="none" stroke={colorClosed} strokeWidth={2}
                      strokeLinejoin="round" strokeLinecap="round" />
                <path d={calc.pathIn} fill="none" stroke={colorIncoming} strokeWidth={2}
                      strokeLinejoin="round" strokeLinecap="round" />

                {}
                {[[calc.iMaxIn, "incoming", colorIncoming], [calc.iMaxCl, "closed", colorClosed]].map(([i, k, c]) => (
                    <g key={k}>
                        <circle cx={calc.x(i)} cy={calc.y(data[i][k])} r={4.5}
                                fill={c} className={classes.dotRing} />
                        <text x={calc.x(i)} y={calc.y(data[i][k]) - 9} textAnchor="middle"
                              className={classes.axisText} style={{ fontWeight: 700 }}>
                            {data[i][k]}
                        </text>
                    </g>
                ))}

                {hover != null && [["incoming", colorIncoming], ["closed", colorClosed]].map(([k, c]) => (
                    <circle key={k} cx={calc.x(hover)} cy={calc.y(punto[k])} r={4.5}
                            fill={c} className={classes.dotRing} />
                ))}
            </svg>

            {punto && (
                <div
                    className={classes.tooltip}
                    style={{
                        left: `${(calc.x(hover) / W) * 100}%`,
                        top: 0,
                        transform: hover > data.length / 2 ? "translateX(-105%)" : "translateX(5%)",
                    }}
                >
                    <div className={classes.tipDay}>{moment(punto.day).format("ddd DD/MM")}</div>
                    <div className={classes.tipRow}>
                        <i className={classes.legendSwatch} style={{ background: colorIncoming }} />
                        {labelIncoming}: <span className={classes.tipNum}>{punto.incoming}</span>
                    </div>
                    <div className={classes.tipRow}>
                        <i className={classes.legendSwatch} style={{ background: colorClosed }} />
                        {labelClosed}: <span className={classes.tipNum}>{punto.closed}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyTrend;
